import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getInventoriesForOrdering,
  calculateOrderSuggestions,
  createOrder,
  sendOrderEmail
} from '../services/ordersService';
import '../pages/css_pages/Ordering.css';
import { FaTimes, FaPaperPlane, FaExclamationTriangle, FaFilter, FaSpinner, FaPlus, FaTrash } from 'react-icons/fa';
import api from '../services/api';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const year  = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day   = String(date.getUTCDate()).padStart(2, '0');
  return `${month}/${day}/${year}`;
};

const fmt = (val, decimals = 2) => {
  const n = parseFloat(val);
  if (isNaN(n)) return '0.00';
  return n.toFixed(decimals);
};

// ============================================
// MERGE VENDOR DUPLICATES
// Colapsa vendors con mismo nombre en uno solo
// ============================================
const mergeVendorGroups = (vendors) => {
  const map = {};
  vendors.forEach(vendor => {
    const key = vendor.vendor_name || 'No Vendor';
    if (!map[key]) {
      map[key] = { ...vendor, products: [...vendor.products] };
    } else {
      // merge productos
      vendor.products.forEach(p => {
        const exists = map[key].products.find(ep => ep.id_product === p.id_product);
        if (!exists) map[key].products.push(p);
      });
      // si el duplicado tiene email y el original no, usar el del duplicado
      if (!map[key].vendor_email && vendor.vendor_email) {
        map[key].vendor_email = vendor.vendor_email;
      }
    }
  });
  return Object.values(map);
};

const Ordering = () => {
  const { selectedStore } = useStore();
  const { user }          = useAuth();

  const [inventories, setInventories]             = useState([]);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [vendorGroups, setVendorGroups]           = useState([]);
  const [prevDate, setPrevDate]                   = useState(null);
  const [loading, setLoading]                     = useState(false);
  const [loadingVendor, setLoadingVendor]         = useState(null);
  const [activeFilter, setActiveFilter]           = useState('all');
  const [sendingAll, setSendingAll]               = useState(false);
  const [groupBy, setGroupBy]                     = useState('category');

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailConfig, setEmailConfig]       = useState({
    to: '', cc: '', subject: '', message: '', vendor: null, items: []
  });
  const [showSendAllModal, setShowSendAllModal] = useState(false);
  const [sendAllConfig, setSendAllConfig]       = useState({ cc: '' });
  const [sendAllProgress, setSendAllProgress]   = useState({ current: 0, total: 0, done: false, errors: [] });

  const [manualMode, setManualMode]               = useState(false);
  const [showManualModal, setShowManualModal]     = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearch, setProductSearch]         = useState('');
  const [loadingProducts, setLoadingProducts]     = useState(false);
  const [manualQty, setManualQty]                 = useState({});

  const groupProductsByCategory = (products) =>
    products.reduce((acc, product) => {
      const cat = product.category_name || 'Uncategorized';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(product);
      return acc;
    }, {});

  const groupProductsByTypeAndCategory = (products) =>
    products.reduce((acc, product) => {
      const type = product.product_type_name || 'Other';
      const cat  = product.category_name     || 'Uncategorized';
      if (!acc[type])      acc[type]      = {};
      if (!acc[type][cat]) acc[type][cat] = [];
      acc[type][cat].push(product);
      return acc;
    }, {});

  const GroupByToggle = () => (
    <div className="pi-groupby-toggle">
      <span className="pi-groupby-label">Group by:</span>
      <button className={`pi-toggle-btn ${groupBy === 'category' ? 'active' : ''}`} onClick={() => setGroupBy('category')} type="button">
        Product Type
      </button>
      <button className={`pi-toggle-btn ${groupBy === 'type' ? 'active' : ''}`} onClick={() => setGroupBy('type')} type="button">
        Category
      </button>
    </div>
  );

  const fetchInventories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getInventoriesForOrdering(selectedStore.id);
      if (response.success) setInventories(response.data);
    } catch (error) {
      console.error('Error fetching inventories:', error);
      alert('Error loading inventories');
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) fetchInventories();
  }, [selectedStore, fetchInventories]);

  const handleInventoryChange = async (e) => {
    const inventoryId = e.target.value;
    if (!inventoryId) {
      setSelectedInventory(null);
      setVendorGroups([]);
      setPrevDate(null);
      setActiveFilter('all');
      return;
    }
    try {
      setLoading(true);
      const inventory = inventories.find(inv => inv.id_inventories === parseInt(inventoryId));
      setSelectedInventory(inventory);
      setActiveFilter('all');
      const response = await calculateOrderSuggestions({
        inventoryId: parseInt(inventoryId),
        storeId: selectedStore.id
      });
      if (response.success) {
        // FIX: colapsar vendors duplicados antes de guardar en estado
        setVendorGroups(mergeVendorGroups(response.data.vendors));
        setPrevDate(response.data.prev_date);
      }
    } catch (error) {
      console.error('Error calculating suggestions:', error);
      alert('Error calculating order suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleActualOrderChange = (vendorIndex, productIndex, value) => {
    const newVendorGroups = [...vendorGroups];
    newVendorGroups[vendorIndex].products[productIndex].actual_order = parseInt(value) || 0;
    setVendorGroups(newVendorGroups);
  };

  const calculateVendorTotals = (vendor) => {
    let totalQuantity = 0;
    let totalAmount   = 0;
    vendor.products.forEach(product => {
      if (product.actual_order > 0) {
        totalQuantity += product.actual_order;
        totalAmount   += product.actual_order * product.unit_price;
      }
    });
    return { totalQuantity, totalAmount };
  };

  const applyFilter = (products) => {
    switch (activeFilter) {
      case 'suggested':   return products.filter(p => p.suggested_order > 0);
      case 'actual':      return products.filter(p => p.actual_order > 0);
      case 'not_counted': return products.filter(p => p.is_missing_from_inventory === true);
      case 'stock_zero':  return products.filter(p => (p.stock_on_hand || 0) === 0);
      default:            return products;
    }
  };

  const handleOpenManualMode = async () => {
    setManualMode(true);
    setSelectedInventory(null);
    setVendorGroups([]);
    setPrevDate(null);
    setActiveFilter('all');
    setProductSearch('');
    setManualQty({});
    try {
      setLoadingProducts(true);
      const res = await api.get('/orders/manual-products', {
        params: { storeId: selectedStore.id }
      });
      if (res.data.success) setAvailableProducts(res.data.data);
    } catch (err) {
      console.error('Error fetching products:', err);
      alert('Error loading products');
    } finally {
      setLoadingProducts(false);
    }
    setShowManualModal(true);
  };

  const handleExitManualMode = () => {
    if (vendorGroups.length > 0) {
      const confirmed = window.confirm('Exit manual order mode? All unsent items will be lost.');
      if (!confirmed) return;
    }
    setManualMode(false);
    setVendorGroups([]);
    setAvailableProducts([]);
    setManualQty({});
    setProductSearch('');
  };

  const handleAddManualProduct = (product) => {
    const qty = parseInt(manualQty[product.id]) || 1;
    if (qty <= 0) { alert('Please enter a valid quantity'); return; }

    const vendorId    = product.vendor_id    || 'no-vendor';
    const vendorName  = product.vendor_name  || 'No Vendor';
    const vendorEmail = product.vendor_email || null;
    const caseSize    = parseFloat(product.case_size) || 1;
    const wholesale   = parseFloat(product.wholesale_price) || 0;
    const orderBy     = product.order_by || product.container_type;
    const unitPrice   = orderBy === 'Case'
      ? wholesale
      : (caseSize > 0 ? wholesale / caseSize : wholesale);

    const entry = {
      id_product:     product.id,
      product_name:   product.name,
      product_code:   product.product_code,
      container_size: product.container_size,
      container_unit: product.container_unit,
      order_by:       orderBy,
      actual_order:   qty,
      suggested_order: 0,
      unit_price:     parseFloat(unitPrice.toFixed(4)),
      stock_on_hand: 0, reorder_point: 0, par: 0, case_size: caseSize,
      is_missing_from_inventory: false,
      category_name: 'Manual', product_type_name: 'Manual',
    };

    setVendorGroups(prev => {
      const groups = prev.map(g => ({ ...g, products: [...g.products] }));
      const vIdx = groups.findIndex(v => v.vendor_id === vendorId);
      if (vIdx >= 0) {
        const pIdx = groups[vIdx].products.findIndex(p => p.id_product === product.id);
        if (pIdx >= 0) {
          groups[vIdx].products[pIdx].actual_order += qty;
        } else {
          groups[vIdx].products.push(entry);
        }
      } else {
        groups.push({ vendor_id: vendorId, vendor_name: vendorName, vendor_email: vendorEmail, products: [entry] });
      }
      return groups;
    });

    setManualQty(prev => ({ ...prev, [product.id]: 1 }));
  };

  const handleRemoveManualProduct = (vendorIndex, productId) => {
    setVendorGroups(prev => {
      const groups = prev.map(g => ({ ...g, products: [...g.products] }));
      groups[vendorIndex].products = groups[vendorIndex].products.filter(
        p => p.id_product !== productId
      );
      return groups.filter(v => v.products.length > 0);
    });
  };

  const renderProductRow = (product, vendorIndex, vendor) => {
    const globalIndex = vendor.products.findIndex(p => p.id_product === product.id_product);
    const varianceClass =
      product.variance > 0.05  ? 'variance-positive' :
      product.variance < -0.05 ? 'variance-negative' : '';

    return (
      <tr key={product.id_product} className={product.is_missing_from_inventory ? 'missing-product' : ''}>
        <td>
          {product.product_name}
          {product.is_missing_from_inventory && (
            <FaExclamationTriangle className="warning-icon" title="Not counted in inventory" />
          )}
        </td>
        <td>{product.product_code || '-'}</td>
        <td>{product.container_size} {product.container_unit}</td>
        <td className="number-cell">
          {product.order_by === 'Case' ? (
            <>
              <span style={{ display: 'block' }}>{fmt(product.opening_last_inv)}</span>
              <span style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({fmt(product.opening_last_inv * product.case_size)})</span>
            </>
          ) : fmt(product.opening_last_inv)}
        </td>
        <td className="number-cell">
          {product.order_by === 'Case' ? (
            <>
              <span style={{ display: 'block' }}>{fmt(product.purchase)}</span>
              <span style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({fmt(product.purchase * product.case_size)})</span>
            </>
          ) : fmt(product.purchase)}
        </td>
        <td className="number-cell">{fmt(product.sold)}</td>
        <td className={`number-cell ${varianceClass}`}>
          {product.order_by === 'Case' ? (
            <>
              <span style={{ display: 'block' }}>{fmt(product.variance)}</span>
              <span style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({fmt(product.variance * product.case_size)})</span>
            </>
          ) : fmt(product.variance)}
        </td>
        <td className="number-cell">
          {product.order_by === 'Case' ? (
            <>
              <span style={{ display: 'block' }}>{fmt(product.stock_on_hand)}</span>
              <span style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({fmt(product.stock_on_hand_raw)})</span>
            </>
          ) : fmt(product.stock_on_hand)}
        </td>
        <td className="number-cell">
          {product.order_by === 'Case' ? (
            <>
              <span style={{ display: 'block' }}>{fmt(product.reorder_point / product.case_size)}</span>
              <span style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({product.reorder_point}/{product.case_size})</span>
            </>
          ) : fmt(product.reorder_point)}
        </td>
        <td className="number-cell">
          {product.order_by === 'Case' ? (
            <>
              <span style={{ display: 'block' }}>{fmt(product.par / product.case_size)}</span>
              <span style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({product.par}/{product.case_size})</span>
            </>
          ) : fmt(product.par)}
        </td>
        <td>{product.order_by || '-'}</td>
        <td className="number-cell suggested">
          {product.order_by === 'Case' ? (
            <>
              <span style={{ display: 'block' }}>{fmt(product.suggested_order, 0)}</span>
              <span style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({fmt(product.suggested_order * product.case_size, 0)})</span>
            </>
          ) : fmt(product.suggested_order, 0)}
        </td>
        <td className="number-cell">
          <input
            type="number" step="1" min="0"
            value={product.actual_order ? product.actual_order : ''}
            placeholder="0"
            onChange={(e) => handleActualOrderChange(vendorIndex, globalIndex, e.target.value)}
            className="actual-order-input"
          />
        </td>
        <td className="number-cell">${fmt(product.unit_price)}</td>
        <td className="number-cell total-cell">
          ${fmt((product.actual_order || 0) * (product.unit_price || 0))}
        </td>
      </tr>
    );
  };

  const renderManualProductRow = (product, vendorIndex) => {
    const productIndex = vendorGroups[vendorIndex].products.findIndex(
      p => p.id_product === product.id_product
    );
    return (
      <tr key={product.id_product}>
        <td>{product.product_name}</td>
        <td>{product.product_code || '-'}</td>
        <td>{product.container_size} {product.container_unit}</td>
        <td>{product.order_by || '-'}</td>
        <td className="number-cell">
          <input
            type="number" step="1" min="1"
            value={product.actual_order}
            onChange={(e) => handleActualOrderChange(vendorIndex, productIndex, e.target.value)}
            className="actual-order-input"
          />
        </td>
        <td className="number-cell">${fmt(product.unit_price)}</td>
        <td className="number-cell total-cell">
          ${fmt((product.actual_order || 0) * (product.unit_price || 0))}
        </td>
        <td>
          <button
            className="ord-btn-remove"
            onClick={() => handleRemoveManualProduct(vendorIndex, product.id_product)}
            title="Remove"
          >
            <FaTrash />
          </button>
        </td>
      </tr>
    );
  };

  const handleSendOrder = (vendor) => {
    if (!vendor.vendor_email) { alert('Vendor has no email address'); return; }
    const itemsToOrder = vendor.products.filter(p => p.actual_order > 0);
    if (itemsToOrder.length === 0) { alert('No products to order'); return; }
    setEmailConfig({
      to:      vendor.vendor_email,
      cc:      '',
      subject: `Purchase Order from ${selectedStore.name}`,
      message: `Dear ${vendor.vendor_name},\n\nPlease find attached our purchase order.\n\nTotal Items: ${itemsToOrder.length}\n\nThank you!`,
      vendor,
      items: itemsToOrder
    });
    setShowEmailModal(true);
  };

  const handleConfirmSendEmail = async () => {
    if (!emailConfig.to) { alert('Please enter recipient email address'); return; }

    if (emailConfig.items.length > 50) {
      const confirmed = window.confirm(
        `⚠️ This order has ${emailConfig.items.length} items.\n\nSending such a large order via email may fail.\n\nWould you like to proceed anyway?`
      );
      if (!confirmed) return;
    }

    const confirmed = window.confirm(
      `Send order to ${emailConfig.to}?\n` +
      `Total Items: ${emailConfig.items.length}\n` +
      `Total Amount: $${emailConfig.items.reduce((sum, item) => sum + (item.actual_order * item.unit_price), 0).toFixed(2)}`
    );
    if (!confirmed) return;

    try {
      setLoadingVendor(emailConfig.vendor.vendor_id);
      const orderData = {
        storeId:     selectedStore.id,
        vendorId:    emailConfig.vendor.vendor_id,
        inventoryId: manualMode ? null : selectedInventory?.id_inventories,
        items:       emailConfig.items,
        notes:       manualMode
          ? 'Manual order'
          : `Order generated from inventory: ${selectedInventory?.inventory_date}`,
        createdBy: user?.id || 1
      };
      const createResponse = await createOrder(orderData);
      if (createResponse.success) {
        const emailPayload   = { ...emailConfig, sentBy: user?.name || 'System' };
        const emailPromise   = sendOrderEmail(createResponse.data.order_id, emailPayload);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('TIMEOUT')), 60000)
        );
        const emailResponse = await Promise.race([emailPromise, timeoutPromise]);
        if (emailResponse.success) {
          alert(
            `Order ${createResponse.data.order_number} sent successfully!\n\n` +
            `Sent to: ${emailConfig.to}\n` +
            `Items: ${emailConfig.items.length}\n` +
            `Total: $${emailConfig.items.reduce((sum, item) => sum + (item.actual_order * item.unit_price), 0).toFixed(2)}`
          );
          setVendorGroups(vendorGroups.map(v =>
            v.vendor_id === emailConfig.vendor.vendor_id
              ? { ...v, products: v.products.map(p => ({ ...p, actual_order: 0 })) }
              : v
          ));
          setShowEmailModal(false);
        }
      }
    } catch (error) {
      console.error('Error sending order:', error);
      if (error.message?.includes('413') || error.response?.status === 413 || error.message?.includes('TIMEOUT')) {
        alert(`Error: The order has ${emailConfig.items.length} items which is too large to send via email.`);
      } else {
        alert('Error sending order: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoadingVendor(null);
    }
  };

  const vendorsWithOrders = vendorGroups.filter(v =>
    v.vendor_email && v.products.some(p => p.actual_order > 0)
  );

  const handleOpenSendAll = () => {
    if (vendorsWithOrders.length === 0) {
      alert('No vendors with orders to send. Please set actual order quantities first.');
      return;
    }
    setSendAllConfig({ cc: '' });
    setSendAllProgress({ current: 0, total: vendorsWithOrders.length, done: false, errors: [] });
    setShowSendAllModal(true);
  };

  const handleConfirmSendAll = async () => {
    try {
      setSendingAll(true);
      const errors = [];
      for (let i = 0; i < vendorsWithOrders.length; i++) {
        const vendor       = vendorsWithOrders[i];
        const itemsToOrder = vendor.products.filter(p => p.actual_order > 0);
        if (itemsToOrder.length > 50) {
          errors.push(`${vendor.vendor_name}: Order has ${itemsToOrder.length} items (skipped - too large)`);
          setSendAllProgress(prev => ({ ...prev, current: i + 1, errors }));
          continue;
        }
        setSendAllProgress(prev => ({ ...prev, current: i + 1 }));
        try {
          const orderData = {
            storeId:     selectedStore.id,
            vendorId:    vendor.vendor_id,
            inventoryId: manualMode ? null : selectedInventory?.id_inventories,
            items:       itemsToOrder,
            notes:       manualMode
              ? 'Manual order'
              : `Order generated from inventory: ${selectedInventory?.inventory_date}`,
            createdBy: user?.id || 1
          };
          const createResponse = await createOrder(orderData);
          if (createResponse.success) {
            const config = {
              to:      vendor.vendor_email,
              cc:      sendAllConfig.cc,
              subject: `Purchase Order from ${selectedStore.name}`,
              message: `Dear ${vendor.vendor_name},\n\nPlease find attached our purchase order.\n\nTotal Items: ${itemsToOrder.length}\n\nThank you!`,
              vendor, items: itemsToOrder, sentBy: user?.name || 'System'
            };
            await sendOrderEmail(createResponse.data.order_id, config);
          }
        } catch (err) {
          console.error(`❌ Error sending to ${vendor.vendor_name}:`, err);
          errors.push(`${vendor.vendor_name}: ${err.message}`);
        }
      }
      setSendAllProgress(prev => ({ ...prev, done: true, errors }));
      setVendorGroups(vendorGroups.map(v => ({
        ...v, products: v.products.map(p => ({ ...p, actual_order: 0 }))
      })));
      if (errors.length > 0) alert(`⚠️ Orders sent with ${errors.length} errors.`);
      else                    alert(`✅ All orders sent successfully! (${vendorsWithOrders.length} vendors)`);
    } catch (error) {
      alert('Error sending orders: ' + error.message);
    } finally {
      setSendingAll(false);
    }
  };

  if (!selectedStore) {
    return (
      <div className="ordering-container">
        <div className="no-store-selected">
          <p>Please select a store to view ordering</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ordering-container">

      <div className="ordering-header">
        <div className="ordering-header-left">
          <h1>Ordering</h1>
          <p className="store-info">Store: {selectedStore.name}</p>
          {manualMode && <span className="ord-manual-badge">Manual Order Mode</span>}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {manualMode && (
            <button className="ord-btn-exit-manual" onClick={handleExitManualMode}>
              <FaTimes /> Exit Manual Mode
            </button>
          )}
          {vendorGroups.length > 0 && (
            <button className="btn-send-all" onClick={handleOpenSendAll} disabled={sendingAll || vendorsWithOrders.length === 0}>
              <FaPaperPlane /> Send All Orders ({vendorsWithOrders.length})
            </button>
          )}
          {!manualMode && (
            <button className="ord-btn-manual" onClick={handleOpenManualMode} disabled={loading}>
              <FaPlus /> Manual Order
            </button>
          )}
        </div>
      </div>

      {!manualMode && (
        <div className="inventory-selector-section">
          <label htmlFor="inventory-select">Select Inventory:</label>
          <select
            id="inventory-select"
            onChange={handleInventoryChange}
            disabled={loading || inventories.length === 0}
            className="inventory-select"
          >
            <option value="">-- Select an inventory --</option>
            {inventories.map(inv => (
              <option key={inv.id_inventories} value={inv.id_inventories}>
                {formatDate(inv.inventory_date)}
              </option>
            ))}
          </select>
          {selectedInventory && prevDate && (
            <span className="date-range-info">
              Purchase period: <strong>{formatDate(prevDate)}</strong> → <strong>{formatDate(selectedInventory.inventory_date)}</strong>
            </span>
          )}
          {selectedInventory && !prevDate && (
            <span className="date-range-info no-prev">
              No previous inventory found — Opening & Purchase will show 0
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="loading-section">
          <FaSpinner className="ord-spinner" />
          <p>Loading...</p>
        </div>
      )}

      {!loading && vendorGroups.length > 0 && !manualMode && (
        <div className="ordering-filters">
          <FaFilter className="filter-icon" />
          <label>Filter:</label>
          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} className="filter-select">
            <option value="all">All products</option>
            <option value="suggested">Suggested order &gt; 0</option>
            <option value="actual">Actual order &gt; 0</option>
            <option value="not_counted">Not counted in inventory</option>
            <option value="stock_zero">Stock on hand = 0</option>
          </select>
          {activeFilter !== 'all' && (
            <button className="filter-clear" onClick={() => setActiveFilter('all')}>
              <FaTimes /> Clear filter
            </button>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <GroupByToggle />
          </div>
        </div>
      )}

      {!loading && vendorGroups.length > 0 && (
        <div className="vendors-section">
          {manualMode && (
            <button className="ord-btn-add-product" onClick={() => setShowManualModal(true)}>
              <FaPlus /> Add Product
            </button>
          )}
          {vendorGroups.map((vendor, vendorIndex) => {
            const totals           = calculateVendorTotals(vendor);
            const filteredProducts = manualMode ? vendor.products : applyFilter(vendor.products);
            if (filteredProducts.length === 0) return null;
            return (
              <div key={`${vendor.vendor_name}-${vendorIndex}`} className="vendor-group">
                <div className="vendor-header">
                  <div className="vendor-info">
                    <h2>{vendor.vendor_name}</h2>
                    {vendor.vendor_email && <span className="vendor-email">{vendor.vendor_email}</span>}
                  </div>
                </div>
                <div className="products-table-container">
                  <table className="products-table">
                    <thead>
                      {manualMode ? (
                        <tr>
                          <th>Product Name</th>
                          <th>Code</th>
                          <th>Container Size</th>
                          <th>Order By</th>
                          <th>Quantity</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                          <th></th>
                        </tr>
                      ) : (
                        <tr>
                          <th>Product<br/>Name</th>
                          <th>Product<br/>Code</th>
                          <th>Container<br/>Size</th>
                          <th title="Stock from previous inventory">Opening<br/>Inv</th>
                          <th title={`Purchases from ${formatDate(prevDate)} to ${formatDate(selectedInventory?.inventory_date)}`}>Purchase</th>
                          <th title="Units sold (not yet available)">Sold</th>
                          <th title="Stock on Hand − (Opening + Purchase) + Sold">Variance</th>
                          <th>Stock on<br/>Hand</th>
                          <th>Reorder<br/>Point</th>
                          <th>Par</th>
                          <th>Order<br/>By</th>
                          <th>Suggested<br/>Order</th>
                          <th>Actual<br/>Order</th>
                          <th>Unit<br/>Price</th>
                          <th>Total</th>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {manualMode
                        ? filteredProducts.map(p => renderManualProductRow(p, vendorIndex))
                        : (groupBy === 'type'
                            ? Object.entries(groupProductsByTypeAndCategory(filteredProducts)).map(([typeName, categories]) => (
                                <React.Fragment key={typeName}>
                                  <tr className="type-header-row"><td colSpan="15"><strong>{typeName}</strong></td></tr>
                                  {Object.entries(categories).map(([catName, catProducts]) => (
                                    <React.Fragment key={`${typeName}-${catName}`}>
                                      <tr className="category-row category-sub-row"><td colSpan="15">&nbsp;&nbsp;&nbsp;{catName}</td></tr>
                                      {catProducts.map(product => renderProductRow(product, vendorIndex, vendor))}
                                    </React.Fragment>
                                  ))}
                                </React.Fragment>
                              ))
                            : Object.entries(groupProductsByCategory(filteredProducts)).map(([category, products]) => (
                                <React.Fragment key={category}>
                                  <tr className="category-row"><td colSpan="15"><strong>{category}</strong></td></tr>
                                  {products.map(product => renderProductRow(product, vendorIndex, vendor))}
                                </React.Fragment>
                              ))
                          )
                      }
                    </tbody>
                  </table>
                </div>
                <div className="vendor-footer">
                  <div className="vendor-totals">
                    <div className="ord-total-item">
                      <span className="ord-total-label">Total Quantity:</span>
                      <span className="ord-total-value">{fmt(totals.totalQuantity)}</span>
                    </div>
                    <div className="ord-total-item">
                      <span className="ord-total-label">Total Amount:</span>
                      <span className="ord-total-value ord-total-amount">${fmt(totals.totalAmount)}</span>
                    </div>
                  </div>
                  <button
                    className="btn-send-order"
                    onClick={() => handleSendOrder(vendor)}
                    disabled={totals.totalQuantity === 0 || loadingVendor === vendor.vendor_id}
                  >
                    {loadingVendor === vendor.vendor_id
                      ? <><FaSpinner className="ord-spinner-small" /> Sending...</>
                      : <><FaPaperPlane /> Send Order to Vendor</>
                    }
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !selectedInventory && !manualMode && (
        <div className="no-inventory-selected">
          <p>Select an inventory to generate order suggestions, or use <strong>Manual Order</strong> to create an order without inventory.</p>
        </div>
      )}

      {!loading && manualMode && vendorGroups.length === 0 && (
        <div className="no-inventory-selected">
          <p>No products added yet. Click <strong>Add Product</strong> to search and add products.</p>
          <button className="ord-btn-add-product" style={{ margin: '16px auto 0', display: 'inline-flex' }} onClick={() => setShowManualModal(true)}>
            <FaPlus /> Add Product
          </button>
        </div>
      )}

      {showManualModal && (
        <div className="ord-modal-overlay">
          <div className="ord-modal-content ord-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="ord-modal-header">
              <h2>Add Products to Order</h2>
              <button className="ord-btn-close" onClick={() => setShowManualModal(false)}><FaTimes /></button>
            </div>
            <div className="ord-modal-body">
              <input
                type="text"
                placeholder="Search by name or code..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="ord-search-input"
                autoFocus
              />
              {loadingProducts ? (
                <div style={{ textAlign: 'center', padding: 32 }}>
                  <FaSpinner className="ord-spinner" />
                  <p style={{ marginTop: 8, color: '#64748b' }}>Loading products...</p>
                </div>
              ) : (
                <div className="ord-product-picker-wrapper">
                  <table className="products-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Code</th>
                        <th>Size</th>
                        <th>Vendor</th>
                        <th>Unit Price</th>
                        <th style={{ width: 90 }}>Qty</th>
                        <th style={{ width: 90 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableProducts
                        .filter(p =>
                          (p.name || '').toLowerCase().includes(productSearch.toLowerCase()) ||
                          (p.product_code && p.product_code.toLowerCase().includes(productSearch.toLowerCase()))
                        )
                        .map(product => {
                          const caseSize  = parseFloat(product.case_size) || 1;
                          const wholesale = parseFloat(product.wholesale_price) || 0;
                          const orderBy   = product.order_by || product.container_type;
                          const unitPrice = orderBy === 'Case'
                            ? wholesale
                            : (caseSize > 0 ? wholesale / caseSize : wholesale);

                          const alreadyAdded = vendorGroups.some(v =>
                            v.products.some(p => p.id_product === product.id)
                          );

                          return (
                            <tr key={product.id} className={alreadyAdded ? 'ord-row-added' : ''}>
                              <td>
                                {product.name}
                                {alreadyAdded && <span className="ord-added-badge"> ✓ added</span>}
                              </td>
                              <td>{product.product_code || '-'}</td>
                              <td>{product.container_size} {product.container_unit}</td>
                              <td>{product.vendor_name || <span style={{ color: '#94a3b8' }}>No vendor</span>}</td>
                              <td className="number-cell">${fmt(unitPrice)}</td>
                              <td>
                                <input
                                  type="number" min="1" step="1"
                                  value={manualQty[product.id] ?? 1}
                                  onChange={(e) => setManualQty(prev => ({ ...prev, [product.id]: e.target.value }))}
                                  className="actual-order-input"
                                  style={{ width: 70 }}
                                />
                              </td>
                              <td>
                                <button className="ord-btn-add-item" onClick={() => handleAddManualProduct(product)}>
                                  <FaPlus /> Add
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      }
                      {availableProducts.filter(p =>
                        (p.name || '').toLowerCase().includes(productSearch.toLowerCase()) ||
                        (p.product_code && p.product_code.toLowerCase().includes(productSearch.toLowerCase()))
                      ).length === 0 && (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>
                            {productSearch ? 'No products found' : 'No products available'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="ord-modal-footer">
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {vendorGroups.reduce((sum, v) => sum + v.products.length, 0)} product(s) in order
              </span>
              <button className="ord-btn-primary" onClick={() => setShowManualModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {showEmailModal && (
        <div className="ord-modal-overlay">
          <div className="ord-modal-content ord-modal-email" onClick={(e) => e.stopPropagation()}>
            <div className="ord-modal-header">
              <h2>Send Order</h2>
              <button className="ord-btn-close" onClick={() => setShowEmailModal(false)}><FaTimes /></button>
            </div>
            <div className="ord-modal-body">
              <div className="email-config-form">
                <div className="sent-by-info">
                  <span className="sent-by-label">Sending as:</span>
                  <span className="sent-by-name">{user?.name || 'System'}</span>
                </div>
                <div className="ord-form-group">
                  <label>TO (VENDOR EMAIL) <span className="ord-required">*</span></label>
                  <input type="email" value={emailConfig.to}
                    onChange={(e) => setEmailConfig({ ...emailConfig, to: e.target.value })}
                    placeholder="vendor@company.com" className="email-input" />
                </div>
                <div className="ord-form-group">
                  <label>CC (OPTIONAL)</label>
                  <input type="email" value={emailConfig.cc}
                    onChange={(e) => setEmailConfig({ ...emailConfig, cc: e.target.value })}
                    placeholder="cc@company.com" className="email-input" />
                  <small className="input-hint">Send a copy to another email address</small>
                </div>
                <div className="ord-form-group">
                  <label>SUBJECT</label>
                  <input type="text" value={emailConfig.subject}
                    onChange={(e) => setEmailConfig({ ...emailConfig, subject: e.target.value })}
                    className="email-input" />
                </div>
                {emailConfig.vendor && (
                  <div className="order-summary">
                    <h4>Order Summary</h4>
                    <p><strong>Vendor:</strong> {emailConfig.vendor.vendor_name}</p>
                    <p><strong>Total Items:</strong> {emailConfig.items.length}</p>
                    <p><strong>Total Amount:</strong> ${emailConfig.items.reduce((sum, item) => sum + (item.actual_order * item.unit_price), 0).toFixed(2)}</p>
                    {emailConfig.items.length > 50 && (
                      <p className="ord-warning-text">Large order ({emailConfig.items.length} items) - may take time to send</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="ord-modal-footer">
              <button className="ord-btn-secondary" onClick={() => setShowEmailModal(false)} disabled={loadingVendor}>Cancel</button>
              <button className="ord-btn-primary" onClick={handleConfirmSendEmail} disabled={loadingVendor || !emailConfig.to}>
                {loadingVendor
                  ? <><FaSpinner className="ord-spinner-small" /> Sending...</>
                  : <><FaPaperPlane /> Send Order</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {showSendAllModal && (
        <div className="ord-modal-overlay">
          <div className="ord-modal-content ord-modal-email" onClick={(e) => e.stopPropagation()}>
            <div className="ord-modal-header">
              <h2>Send All Orders</h2>
              {!sendingAll && !sendAllProgress.done && (
                <button className="ord-btn-close" onClick={() => setShowSendAllModal(false)}><FaTimes /></button>
              )}
            </div>
            <div className="ord-modal-body">
              {!sendAllProgress.done ? (
                <div className="email-config-form">
                  <div className="sent-by-info">
                    <span className="sent-by-label">Sending as:</span>
                    <span className="sent-by-name">{user?.name || 'System'}</span>
                  </div>
                  <p className="send-all-info">
                    This will send orders to <strong>{vendorsWithOrders.length} vendor(s)</strong>:
                  </p>
                  <ul className="send-all-vendors-list">
                    {vendorsWithOrders.map(v => {
                      const itemCount = v.products.filter(p => p.actual_order > 0).length;
                      return (
                        <li key={v.vendor_id}>
                          <strong>{v.vendor_name}</strong> — {v.vendor_email}
                          <span className={`send-all-items ${itemCount > 50 ? 'warning' : ''}`}>
                            ({itemCount} items{itemCount > 50 ? ' - LARGE ORDER!' : ''})
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="ord-form-group">
                    <label>CC (OPTIONAL)</label>
                    <input type="email" value={sendAllConfig.cc}
                      onChange={(e) => setSendAllConfig({ ...sendAllConfig, cc: e.target.value })}
                      placeholder="cc@company.com" className="email-input"
                      disabled={sendingAll} />
                  </div>
                  {sendingAll && (
                    <div className="send-all-progress">
                      <div className="ord-progress-bar">
                        <div className="ord-progress-fill"
                          style={{ width: `${(sendAllProgress.current / sendAllProgress.total) * 100}%` }} />
                      </div>
                      <p>Sending {sendAllProgress.current} of {sendAllProgress.total}...</p>
                      {sendAllProgress.errors.length > 0 && <p>Errors so far: {sendAllProgress.errors.length}</p>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="send-all-results">
                  <p className="send-all-success">
                    ✅ Orders sent to {sendAllProgress.total - sendAllProgress.errors.length} of {sendAllProgress.total} vendors.
                  </p>
                  {sendAllProgress.errors.length > 0 && (
                    <div className="send-all-errors">
                      <p><strong>Errors:</strong></p>
                      {sendAllProgress.errors.map((err, i) => (
                        <p key={i} className="send-all-error-item">• {err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="ord-modal-footer">
              {!sendAllProgress.done ? (
                <>
                  <button className="ord-btn-secondary" onClick={() => setShowSendAllModal(false)} disabled={sendingAll}>Cancel</button>
                  <button className="ord-btn-primary" onClick={handleConfirmSendAll} disabled={sendingAll}>
                    {sendingAll
                      ? <><FaSpinner className="ord-spinner-small" /> Sending {sendAllProgress.current}/{sendAllProgress.total}...</>
                      : <><FaPaperPlane /> Send All Orders</>
                    }
                  </button>
                </>
              ) : (
                <button className="ord-btn-primary" onClick={() => setShowSendAllModal(false)}>Close</button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Ordering;